import router from '@/routers'
import { availability, product as service } from '@/services/axios'
import Vue from 'vue'

const situations = [
    {
        label: 'Total',
        situation: 'total',
        bg_color: '#DEDFDF'
    },
    {
        label: 'Disponível',
        situation: 'available',
        bg_color: '#4EDB54'
    },
    {
        label: 'Vendido',
        situation: 'sale',
        bg_color: '#FF081C'
    },
    {
        label: 'Pré-reserva',
        situation: 'pre-reserve',
        bg_color: '#F3D148'
    },
    {
        label: 'Reservado',
        situation: 'reserved',
        bg_color: '#FB8120'
    },
    {
        label: 'Reserva Permanente',
        situation: 'reserved-permanetly',
        bg_color: '#939393'
    },
    {
        label: 'Indisponível',
        situation: 'unavailable',
        bg_color: '#9B59B6'
    }
]

export const product = {
    namespaced: true,

    state: {
        data: {
            id: null,
            units: []
        },
        paginations: null,
        params: null,
		revenue: {
			expected: 0,
			earned: 0
		},
        loading: {
			general: false,
            blocks: {}
        },
        teamsListUsersProduct: {}
    },

    getters: {
        /*
		* Retorna os times por usuários
		*/
        getTeamsListUsersProduct(state) {
            return state.teamsListUsersProduct
        },

        getProduct(state) {
            return state.data
        },
		/*
		* Retorna a receita de forma separada pois o produto estava sendo sobrescrito
		*/
		getRevenue(state) {
            return state.revenue
        },
        getVisualizationParams(state) {
            return state.params
        },
        /**
         * Retorna as configurações de paginação definidas
         *
         * @returns Object
         */
        getVisualizationPaginations(state) {
            return state.paginations
        },
        /**
         * Retorna um objeto contendo os status de loading
         *
         * @returns Object
         */
        getLoading(state) {
            return state.loading
        },
        /**
         * Retorna um objeto contendo todas as configurações de situação definidas pela
         * API
         *
         * @returns Object|JSON
         */
        getSituations(state) {
            return state.data.situations || {}
        },
        /**
         * Faz o parse dos dados do resumo e retorna juntamente com labels, cores e porcentagens
         *
         * @returns Object|JSON
         */
        getSummary(state) {
            return state.data?.summary
            ? JSON.parse(JSON.stringify(situations))
                .map(item => {
                    const value = state.data?.summary[item.situation] || 0
                    item.value = value
                    item.percent = state.data.summary.total
                        ? parseFloat((value / state.data.summary.total) * 100).toFixed(2)
                        : 0
                    item.value_label = `${item.value} (${item.percent}%)`
                    // Pega as configurações de cor e label para todas as situações/status de unidade
                    if (state.data?.situations[item.situation]) {
                        item.bg_color = state.data?.situations[item.situation]?.color
                        item.label = state.data?.situations[item.situation]?.name
                    }

                    return item
                })
            : []
        },
        /**
         * Retorna os dados necessários para mostrar a visualização desejada
         *
         * @returns Object
         */
        getVisualization(state, getters) {
            // É necessário copiar como JSON para não manter referência aos objetos originais
            let data = {
                summary: [...getters.getSummary]
            }
            /**
             * Verifica se será mostrado o label que diferencia o tipo da propriedade
             * (Obs.: Quadra, Lote, Torre ou Unidade)
             */
            const showProperty = !getters.getProduct?.infos?.multiProperty?.hideNumberAndBlock

            if (state.data?.availability?.type === 'vertical') {
                data = {
                    ...data,
                    towers: Object.values(state.data?.filters?.blocks || []).map(tower => {
                        return {
                            enterprise: showProperty
                                ? `Torre ${tower}`
                                : tower,
                            number: tower,
                            active: state.params?.filter?.blocks
                                ? state.params?.filter?.blocks === tower
                                : state.data?.filters?.blocks[0] === tower
                        }
                    }),
                    floors: {
                        overground: [],
                        underground: []
                    }
                }

                /**
                 * Define uma variável para guardar o andar mais alto que possui unidades,
                 * começando pelo andar mais baixo possível (subsolo ou térreo)
                 */
                let higherNonEmptyFloor = 0 - parseInt(state.data.infos.underground || 0)
                for (let floor in [...Array(parseInt(state.data.infos.floors)).keys()]) {
                    // Verifica se existem andares no subsolo
                    floor -= parseInt(state.data.infos.underground || 0)
                    // Verifica se será mostrado o andar de térreo
                    if (floor >= 0 &&
                        !(state.data.infos.groundFloor && Boolean(state.data.infos.groundFloor))) {
                        floor += 1
                    }

                    // Busca as unidades do andar processado
                    let units = state.data.availability.units
                        .filter(unit => {
                            return parseInt(unit.floor) === floor && unit.status === 'active'
                        })
                        .map(unit => {
                            unit.bg_color = unit.status === 'active'
                                ? (state.data?.situations[unit.situation]?.color || '#400000')
                                : '#dfe3ea'

                            return unit
                        })

                    if (units.length || !state.data.infos.show_unregister_floor_empty) {
                        const type = floor >= 0 ? 'overground' : 'underground'
                        // Verifica se o andar atual possúi unidades
                        if (units.length) higherNonEmptyFloor = floor

                        if (!state.data.infos.show_unregister_units_empty) {
                            // Calcula a quantidade máxima de unidades por andar
                            const unitsByFloor = Math.ceil(
                                parseInt(state.data.infos.units) / parseInt(state.data.infos.floors)
                            )
                            // Gera as unidades vazias para cada andar
                            units = [...Array(unitsByFloor).keys()].map(index => {
                                // Verifica se no andar atual existe uma unidade cadastrada para a posição atual
                                const unit = units.find(unit => unit.pos_x === index + 1)

                                return unit || {
                                    number: '-',
                                    status: 'inactive',
                                    situation: 'unavailable',
                                    pos_x: index + 1,
                                    floor,
                                    cost: null,
                                    bg_color: '#dfe3ea'
                                }
                            })
                        }

                        data.floors[type].push({
                            label: floor ? `${floor}°` : 'T',
                            number: floor,
                            units
                        })
                    }
                }

                /**
                 * Caso a configuração que oculta os andares altos vazios estiver ativa, oculta
                 * todos os andares acima do andar mais alto que possúi unidades
                 */
                if (state.data?.infos?.show_unregister_floor) {
                    for (const type in data.floors) {
                        data.floors[type] = data.floors[type].filter(floor => floor.number <= higherNonEmptyFloor)
                    }
                }

            } else if (state.data?.availability?.type === 'horizontal') {
                data.blocks = Object.values(state.data?.filters?.blocks || [])
                    .map(block => {
                        // Busca as unidades da quadra
                        const units = Object.values(state.data.availability?.units || [])
                            .filter(unit => unit.block === block)
                            .map(unit => {
                                unit.bg_color = unit.status === 'active'
                                    ? (state.data?.situations[unit.situation]?.color || '#400000')
                                    : '#dfe3ea'
                                unit.situation_label = state.data?.situations[unit.situation]?.name

                                return unit
                            })

                        // Verifica se existem informações customizadas para a quadra
                        const blockInfo = Object.values(state.data?.infos?.informationBlocks || [])
                            .find(info => {
                                return info.blockName === block
                            })

                        /**
                         * Monta o objeto contendo todas as informações relevantes das quadras.
                         * Obs.: É necessário copiar como JSON para não manter referência aos objetos
                         * originais
                         */
                        return {
                            label: showProperty
                                ? `Quadra ${blockInfo?.blockName || block}`
                                : (blockInfo?.blockName || block),
                            block,
                            stage: blockInfo?.stage || state.data?.infos?.stage,
                            units,
                            summary: state.data?.summary?.blocks && state.data?.summary?.blocks[block]
                                ? JSON.parse(JSON.stringify(situations))
                                    .map(item => {
                                        item.value = state.data?.summary?.blocks[block][item.situation] || 0
                                        item.label = state.data?.situations[item.situation]?.name || item.label

                                        return item
                                    })
                                : [{ label: 'Total', value: 0, percent: 100.00, bg_color: '#DEDFDF' }]
                        }
                    })
            }

            return data
        }
    },

    mutations: {
        SET_TEAMS_LIST_USERS_PRODUCT(state, payload) {
            const userId = payload.id

            if (!state.teamsListUsersProduct[userId]) {
                state.teamsListUsersProduct[userId] = []
            }

            // Junta os times antigos com os novos
            const combined = [...state.teamsListUsersProduct[userId], ...payload.teams]

            // Remove duplicatas por ID de time
            const unique = []
            const seen = new Set()

            for (const team of combined) {
                if (!seen.has(team.id)) {
                    seen.add(team.id)
                    unique.push(team)
                }
            }

            state.teamsListUsersProduct[userId] = unique
        },
        SET_PRODUCT(state, payload) {
            state.data = { ...payload }
        },
		SET_REVENUE(state, payload) {
            state.revenue = { ...state.revenue, ...payload }
        },
        SET_VISUALIZATION_PARAMS(state, payload) {
            state.params = { ...payload }
        },
        SET_BLOCK_PAGINATION(state, payload) {
            let paginations = { ...state.paginations || {} }
            paginations[payload.block] = { ...payload.pagination }
            state.paginations = { ...paginations }
        },
        SET_GENERAL_LOADING(state, payload) {
            state.loading.general = payload
        },
        SET_BLOCK_LOADERS(state, payload) {
            state.loading.blocks = Object.fromEntries(payload.map(item => [item, false]))
        },
        SET_BLOCK_LOADING(state, payload) {
            state.loading.blocks[payload.block] = payload.status
        },
        CLEAR_VISUALIZATION(state) {
            state.data = { id: null, units: [] }
            state.paginations = null
            state.params = null
            state.loading = {
                general: false,
                blocks: {}
            }
        },
        UPDATE_SUMMARY(state, { oldSituation, newSituation }) {
            // Atualizar contadores das situações
            if (state.data.summary[oldSituation] > 0) {
                state.data.summary[oldSituation] -= 1;
            }
            if (state.data.summary[newSituation] != null) {
                state.data.summary[newSituation] += 1;
            } else {
                state.data.summary[newSituation] = 1;
            }

        },
    },

    actions: {
        setTeamsListUsersProduct({ commit }, payload) {
            commit('SET_TEAMS_LIST_USERS_PRODUCT', payload)
        },
        /**
         * Sta no state os dados do produto
         *
         * @param {Object} payload Objeto JSON contendo os dados do produto
         */
        setProduct({ commit }, payload) {
            commit('SET_PRODUCT', payload)
        },
		setRevenue({ commit }, payload) {
            commit('SET_REVENUE', payload)
        },
         // Nova ação para atualizar product e summary com base no unityEvent
        updateSummary({ commit }, { unityEvent, units }) {
            const unitToUpdate = units.find(unit => unit.id === unityEvent.id)
            if (unitToUpdate) {
                // Atualizar o summary com base na situação antiga e nova
                commit('UPDATE_SUMMARY', {
                    oldSituation: unitToUpdate.situation,
                    newSituation: unityEvent.situation
                });

            }
        },
        /**
         * Seta no state os parâmetros de filtragem para a visualização
         *
         * @param {Object} payload Objeto JSON contendo os parâmetros de filtragem para a visualização
         */
        setVisualizationParams({ commit }, payload) {
            commit('SET_VISUALIZATION_PARAMS', payload)
        },
        /**
         * Seta o loading para uma quadra/torre específica
         *
         * @param {Object} payload Dados indicando a quadra/torre e o status do loading
         */
        setBlockLoading({ commit }, payload) {
            commit('SET_BLOCK_LOADING', payload)
        },
        /**
         * Seta os dados de paginação para uma quadra/torre específica
         *
         * @param {Object} payload Dados de paginação para uma quadra/torre
         */
        setBlockPagination({ commit }, payload) {
            commit('SET_BLOCK_PAGINATION', payload)
        },
        /**
         * Carrega os dados necessários para mostrar as unidades do produto
         *
         * @param {String|Number} payload ID do produto em questão
         */
        loadVisualization({ dispatch, commit, getters }, payload) {
            // Busca os parâmetros para a busca, inclusive de filtragem
            let syncValue = router.currentRoute.query.sync

            let params = getters.getVisualizationParams
                ? { ...getters.getVisualizationParams }
                : {
                    visualization: 'digitalmirror',
                    header: true,
                    sync: syncValue
                }

            // Caso tenha sido executada uma filtragem por torre/quadra, guarda o identificador
            const block = params.filter?.blocks
            commit('SET_VISUALIZATION_PARAMS', params)

            // Formata os filtros para enviar na requisição no formato Query String
            if (params.filter) {
                params.filter = Object.keys(params.filter)
                    .filter(filter => {
                        // Filtra as opções de filtragem para não enviar parâmetros indesejados
                        return [
                            'page',
                            'per_page',
                            'blocks',
                            'situations',
                            'order',
                            'orderby',
                            'current_page'
                        ].includes(filter)
                    })
                    .map(filter => {
                        let value = params.filter[filter]
                        if (typeof value === 'object' &&
                            Object.prototype.hasOwnProperty.call(value, 'length')) {
                            value = value.join(',')
                        }

                        return `${filter}:${value}`
                    })
                    .join(',')
            }

            // Ativa o loading geral, apenas quando forem buscados os dados completos do empreendimento
            commit('SET_GENERAL_LOADING', params.header)
            if (block) commit('SET_BLOCK_LOADING', { block, status: true })
            service.getV2(payload, 'units', params)
                .then(response => {
                    if (!response.data.data) {
                        Vue.prototype.$toastr.e('Este empreendimento não possui uma disponibilidade válida!')
                        router.push({ name: 'products.list' })
                        return
                    }

                    let product = { ...getters.getProduct || {} }
                    const data = { ...response.data.data }

                    /**
                     * Verifica se fora executada uma filtragem e, caso tenha sido, salva os dados de
                     * paginação
                     */
                    if (block && data.pagination) {
                        commit('SET_BLOCK_PAGINATION', { block, pagination: { ...data.pagination } })
                    }

                    // Caso o parâmetro header seja true, subrescreve as informações de produto
                    if (params.header) {
                        product = { ...data }
                        // Para os empreendimentos horizontais, seta-se os loaders das quadras
                        if (product?.availability?.type === 'horizontal' || product?.availability?.type === 'vertical') {
                            commit('SET_BLOCK_LOADERS', product.filters.blocks)
                        }

                        // Para empreendimentos integração V1, solicita o sincronismo das unidades
                        if (product?.availability?.integration_erp_id &&
                            product.availability?.integration_erp.version !== 'v2') {
                            availability.getBlocks(product.availability_id)
                        }

                    /**
                     * Caso o empreendimento buscado seja horizontal, adiciona as unidades retornadas
                     * às unidades já listadas
                     */
                    } else if (product?.availability?.type === 'horizontal') {
                        product.availability.units = [
                            ...product.availability.units || [],
                            ...data.availability.units || []
                        ]
                    /**
                     * Caso o empreendimento buscado seja vertical, adiciona as unidades retornadas
                     */
                    } else if (product?.availability?.type === 'vertical') {
                        product.availability.units = [
                            ...data.availability.units || []
                        ]
                    }

                    commit('SET_PRODUCT', product)
                })
                .catch(error => {
                    if (error?.data?.msg && error?.data?.details?.hasPermission !== false) {
                        Vue.prototype.$toastr.e(error.data.msg)
                        dispatch('detailsHasPermissions/updateDetails', error?.data?.msg, { root: true })
                        router.push({ name: 'products.dataUnits' })
                    }

                    if (!error.data.details.hasPermission) {
                        dispatch('detailsHasPermissions/updateDetails', error?.data?.details, { root: true })
                        router.push({ name: 'products.dataUnits' })
                    }
                })
                .finally(() => {
                    commit('SET_GENERAL_LOADING', false)
                    if (block) commit('SET_BLOCK_LOADING', { block, status: false })
                })
        },
        /**
         * Chama a mutation responsável por limpar o state para a próxima visualização
         */
        clearVisualization({ commit }) {
            commit('CLEAR_VISUALIZATION')
        }
    }
}